import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Pressable,
  Image,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { useAppSelector } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { useResetPasswordForm } from '@/features/auth/useResetPasswordForm';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { COLORS } from '@/constants/residents';

// Import native version for mobile viewport on web
const ResetPasswordScreenNative = require('./ResetPasswordScreen.native').ResetPasswordScreen;

export const ResetPasswordScreen: React.FC = (props) => {
  const { layoutMode } = useResponsive();
  
  // On mobile/tablet viewports, show native component
  if (layoutMode !== 'desktop') {
    return <ResetPasswordScreenNative {...props} />;
  }
  
  // Desktop viewport - render the desktop web UI below
  return <ResetPasswordScreenDesktop {...props} />;
};

const ResetPasswordScreenDesktop: React.FC = () => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const { showSuccess, showError } = useToast();
  
  // Get admin mode from auth state
  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;
  
  // Hover states
  const [submitHovered, setSubmitHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  // Use custom hook for all form logic
  const form = useResetPasswordForm(showError, showSuccess);

  const {
    password,
    confirmPassword,
    isLoading,
    formError,
    showPassword,
    showConfirmPassword,
    setPassword,
    setConfirmPassword,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleSubmit,
    handleCancel,
    passwordRequirements,
  } = form;

  // Dynamic styles based on responsive breakpoints and admin mode
  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident,
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isTablet && styles.splitContainerTablet,
      isDesktop && styles.splitContainerDesktop,
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isTablet && styles.imagePaneTablet,
      isDesktop && styles.imagePaneDesktop,
    ],
    formPane: [
      styles.formPane,
      isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      isMobile && styles.formPaneMobile,
      isTablet && styles.formPaneTablet,
      isDesktop && styles.formPaneDesktop,
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isTablet && styles.formInnerTablet,
      isDesktop && styles.formInnerDesktop,
    ],
    title: [
      styles.title,
      isAdminMode ? styles.titleAdmin : styles.titleResident,
      isMobile && styles.titleMobile,
      isTablet && styles.titleTablet,
      isDesktop && styles.titleDesktop,
    ],
  };

  return (
    <View style={dynamicStyles.pageWrapper}>
      <AuthHeader show={true} />

      <View style={dynamicStyles.splitContainer}>
        {/* Left side - Image pane (hidden on mobile, visible on desktop) */}
        <View style={dynamicStyles.imagePane}>
          <Image
            source={isAdminMode ? require('@/assets/images/login.png') : require('@/assets/images/sign-up.png')}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
        
        {/* Right side - Form pane */}
        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            {/* Title */}
            <Text style={dynamicStyles.title}>Reset your password</Text>
            
            {/* Description */}
            <Text style={styles.description}>
              Enter your new password below.
            </Text>

            {/* Password Requirements */}
            <Text style={styles.requirements}>
              Password must be between {passwordRequirements.min} and {passwordRequirements.max} characters.
            </Text>

            {/* Form Error */}
            {formError && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{formError}</Text>
              </View>
            )}

            {/* Password Input */}
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
              value={password}
              onChangeText={setPassword}
              showPassword={showPassword}
              onToggleVisibility={toggleShowPassword}
              editable={!isLoading}
            />

            {/* Confirm Password Input */}
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm new password"
              placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              showPassword={showConfirmPassword}
              onToggleVisibility={toggleShowConfirmPassword}
              editable={!isLoading}
              onSubmitEditing={handleSubmit}
            />

            {/* Button Container */}
            <View style={styles.buttonContainer}>
              <Pressable
                style={({ pressed }) => [
                  styles.cancelButton,
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonHovered,
                  Platform.OS === 'web' && pressed && styles.cancelButtonPressed,
                ]}
                onPress={handleCancel}
                disabled={isLoading}
                onHoverIn={Platform.OS === 'web' ? () => setCancelHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setCancelHovered(false) : undefined}
              >
                <Text style={[
                  styles.cancelButtonText,
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonTextHovered,
                ]}>
                  Cancel
                </Text>
              </Pressable>

              <Pressable
                style={({ pressed }) => [
                  styles.submitButton,
                  isLoading && styles.submitButtonDisabled,
                  Platform.OS === 'web' && submitHovered && !isLoading && styles.submitButtonHovered,
                  Platform.OS === 'web' && pressed && !isLoading && styles.submitButtonPressed,
                ]}
                onPress={handleSubmit}
                disabled={isLoading}
                onHoverIn={Platform.OS === 'web' ? () => setSubmitHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setSubmitHovered(false) : undefined}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text style={styles.submitButtonText}>
                    Reset Password
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export const ResetPasswordDesktopContent: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;

  const [submitHovered, setSubmitHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  const form = useResetPasswordForm(showError, showSuccess);
  const {
    password, confirmPassword, isLoading, formError,
    showPassword, showConfirmPassword,
    setPassword, setConfirmPassword,
    toggleShowPassword, toggleShowConfirmPassword,
    handleSubmit, handleCancel, passwordRequirements,
  } = form;

  const titleStyle = [styles.title, styles.titleDesktop, isAdminMode ? styles.titleAdmin : styles.titleResident];

  return (
    <View style={desktopContentStyles.formInner}>
      <Text style={titleStyle}>Reset your password</Text>

      <Text style={styles.description}>Enter your new password below.</Text>

      <Text style={styles.requirements}>
        Password must be between {passwordRequirements.min} and {passwordRequirements.max} characters.
      </Text>

      {formError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{formError}</Text>
        </View>
      )}

      <PasswordInput
        label="New Password"
        placeholder="Enter new password"
        placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
        value={password}
        onChangeText={setPassword}
        showPassword={showPassword}
        onToggleVisibility={toggleShowPassword}
        editable={!isLoading}
      />

      <PasswordInput
        label="Confirm Password"
        placeholder="Confirm new password"
        placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
        value={confirmPassword}
        onChangeText={setConfirmPassword}
        showPassword={showConfirmPassword}
        onToggleVisibility={toggleShowConfirmPassword}
        editable={!isLoading}
        onSubmitEditing={handleSubmit}
      />

      <View style={styles.buttonContainer}>
        <Pressable
          style={({ pressed }) => [
            styles.cancelButton,
            cancelHovered && styles.cancelButtonHovered,
            pressed && styles.cancelButtonPressed,
          ]}
          onPress={handleCancel}
          disabled={isLoading}
          onHoverIn={() => setCancelHovered(true)}
          onHoverOut={() => setCancelHovered(false)}
        >
          <Text style={[styles.cancelButtonText, cancelHovered && styles.cancelButtonTextHovered]}>Cancel</Text>
        </Pressable>

        <Pressable
          style={({ pressed }) => [
            styles.submitButton,
            isLoading && styles.submitButtonDisabled,
            submitHovered && !isLoading && styles.submitButtonHovered,
            pressed && !isLoading && styles.submitButtonPressed,
          ]}
          onPress={handleSubmit}
          disabled={isLoading}
          onHoverIn={() => setSubmitHovered(true)}
          onHoverOut={() => setSubmitHovered(false)}
        >
          {isLoading ? (
            <ActivityIndicator color="#FFFFFF" size="small" />
          ) : (
            <Text style={styles.submitButtonText}>Reset Password</Text>
          )}
        </Pressable>
      </View>
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const styles = StyleSheet.create({
  // Base styles
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  pageWrapperAdmin: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  pageWrapperResident: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },

  // Layout styles
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  splitContainerTablet: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 768,
  },
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },

  imagePane: {
    ...Platform.select({
      web: {
        display: 'flex' as any,
        width: 384,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative' as any,
      },
      default: {
        display: 'none',
      },
    }),
  },
  imagePaneMobile: {
    display: 'none',
  },
  imagePaneTablet: {
    display: 'none',
  },
  imagePaneDesktop: {
    display: 'flex' as any,
    width: 384,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative' as any,
  },
  image: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      objectFit: 'cover' as any,
      position: 'absolute' as any,
      top: 0,
      left: 0,
    }),
  },

  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'flex-start', default: 'flex-start' }),
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'auto' as any,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  formPaneAdmin: {
    backgroundColor: '#171717',
  },
  formPaneResident: {
    backgroundColor: '#171717',
  },
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  formPaneTablet: {
    width: '100%',
    maxWidth: 512,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'flex-start',
    flexShrink: 0,
    overflow: 'auto' as any,
  },

  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 40, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 40,
        paddingBottom: 40,
        minHeight: '100%' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        justifyContent: 'center' as any,
      },
    }),
  },
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  formInnerTablet: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    maxWidth: 480,
  },
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    maxWidth: 512,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },

  // Content styles
  title: {
    fontSize: Platform.select({ web: 30, default: 24 }),
    fontWeight: '600',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    marginBottom: 24,
    marginTop: 24,
  },
  titleAdmin: {
    color: '#eef1f4',
  },
  titleResident: {
    color: '#eef1f4',
  },
  titleMobile: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleTablet: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleDesktop: {
    fontSize: 30,
    textAlign: 'left',
    marginBottom: 24,
  },

  description: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    lineHeight: 20,
    marginBottom: 12,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  requirements: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    lineHeight: 18,
    marginBottom: 24,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  // Error container
  errorContainer: {
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  errorText: {
    color: '#DC2626',
    fontSize: 14,
    lineHeight: 20,
  },

  // Button styles
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 8,
  },
  cancelButton: {
    minWidth: 120,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
      },
    }),
  },
  cancelButtonHovered: {
    backgroundColor: '#FFFFFF',
    borderColor: '#FFFFFF',
  },
  cancelButtonPressed: {
    backgroundColor: '#F3F4F6',
    borderColor: '#F3F4F6',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  cancelButtonTextHovered: {
    color: '#374151',
  },
  submitButton: {
    minWidth: 120,
    backgroundColor: COLORS.BUTTON_SECONDARY,
    borderWidth: 1,
    borderColor: COLORS.BUTTON_SECONDARY,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
      },
    }),
  },
  submitButtonHovered: {
    backgroundColor: COLORS.BUTTON_SECONDARY,
    borderColor: COLORS.BUTTON_SECONDARY,
  },
  submitButtonPressed: {
    backgroundColor: COLORS.BUTTON_SECONDARY,
    borderColor: COLORS.BUTTON_SECONDARY,
  },
  submitButtonDisabled: {
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
});
